import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as simpleIcons from 'simple-icons';
import Navbar from './Navbar';
import SEO from './SEO';
import Transition from './Transition';
import SplitText from './SplitText';
import FAQSection from './FAQSection';
import ContactSection from './ContactSection';
import { services } from '../data/services';

const splitTextFrom = { opacity: 0, y: 60 };
const splitTextTo = { opacity: 1, y: 0 };

const slugToAnchor = (slug) =>
  slug.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Per-block signature accent so each service reads as its own "world"
// rather than only alternating dark/cream.
const SERVICE_ACCENTS = ['#b1c1ef', '#f2acac', '#ffdd94', '#f97b22'];

// Looks up a simple-icons SVG by its slug, returning just the inner <path …/>
// so we can drop it inside our own <svg> wrapper and tint with currentColor.
const getSimpleIcon = (slug) => {
  if (!slug) return null;
  const key = `si${slug.charAt(0).toUpperCase()}${slug.slice(1)}`;
  return simpleIcons[key] || null;
};

const ToolLogo = ({ tool, isDark }) => {
  // Hairline 0.25px outline so the chips visually anchor against either
  // background without looking heavy.
  const outlineColor = isDark ? '#f2f3d9' : '#262624';
  const baseChip =
    'w-10 h-10 sm:w-11 sm:h-11 lg:w-12 lg:h-12 rounded-full flex items-center justify-center shadow-sm flex-shrink-0';
  const outlineStyle = { boxShadow: `0 0 0 0.25px ${outlineColor}` };

  // Adobe-style monogram chip — used for tools whose marks aren't in
  // simple-icons (Adobe CC apps + Procreate). Adobe's own marks are
  // two-letter monograms so this still reads as the actual app brand.
  if (tool.abbr) {
    return (
      <span
        title={tool.name}
        aria-label={tool.name}
        style={{ backgroundColor: tool.bg, color: tool.fg, ...outlineStyle }}
        className={`${baseChip} font-bricolage font-extrabold text-[13px] sm:text-[14px] lg:text-[15px] tracking-[-0.02em] leading-none`}
      >
        {tool.abbr}
      </span>
    );
  }

  // Real brand logo from simple-icons, tinted with the tool's brand colour.
  const icon = getSimpleIcon(tool.iconSlug);
  const pathMatch = icon ? icon.svg.match(/<path[^>]*\/>/) : null;
  return (
    <span
      title={tool.name}
      aria-label={tool.name}
      style={{ backgroundColor: '#f2f3d9', ...outlineStyle }}
      className={baseChip}
    >
      {pathMatch && (
        <svg
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 sm:w-[22px] sm:h-[22px] lg:w-6 lg:h-6"
          fill={tool.brand || `#${icon.hex}`}
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: pathMatch[0] }}
        />
      )}
    </span>
  );
};

const TierCard = ({ tier, slug, isDark }) => {
  // On dark service blocks the tier theme inverts so cards still pop.
  const cardClasses = tier.featured
    ? isDark
      ? 'bg-licht text-mainkleur border-2 border-licht shadow-xl shadow-mainkleur/40'
      : 'bg-mainkleur text-licht border-2 border-mainkleur shadow-xl shadow-mainkleur/20'
    : isDark
      ? 'bg-licht/10 text-licht border-2 border-licht/25 hover:border-licht/60 transition-colors duration-300'
      : 'bg-white/40 text-mainkleur border-2 border-mainkleur/20 hover:border-mainkleur/60 transition-colors duration-300';

  const timelineColor = tier.featured
    ? (isDark ? 'text-mainkleur/60' : 'text-licht/60')
    : (isDark ? 'text-licht/60' : 'text-mainkleur/60');

  const bestForColor = tier.featured
    ? (isDark ? 'text-mainkleur/70' : 'text-licht/70')
    : (isDark ? 'text-licht/70' : 'text-mainkleur/70');

  const dividerColor = tier.featured
    ? (isDark ? 'bg-mainkleur/15' : 'bg-licht/20')
    : (isDark ? 'bg-licht/15' : 'bg-mainkleur/15');

  const checkColor = tier.featured
    ? 'text-[#f97b22]'
    : (isDark ? 'text-[#fe9043]' : 'text-mainkleur');

  const ctaClasses = tier.featured
    ? 'bg-[#f97b22] hover:bg-[#fe9043] text-mainkleur'
    : isDark
      ? 'bg-licht text-mainkleur hover:bg-licht/90'
      : 'bg-mainkleur text-licht hover:bg-mainkleur/90';

  return (
    <div className={`relative rounded-2xl p-6 sm:p-7 lg:p-8 flex flex-col h-full ${cardClasses}`}>
      {tier.featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f97b22] text-mainkleur px-4 py-1.5 rounded-full text-xs font-space font-bold uppercase tracking-[0.18em] whitespace-nowrap">
          Most popular
        </span>
      )}

      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h4 className="font-bricolage font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">
          {tier.name}
        </h4>
        <span className={`font-space text-xs uppercase tracking-[0.18em] ${timelineColor}`}>
          {tier.timeline}
        </span>
      </div>

      <div className="font-bricolage font-extrabold text-3xl sm:text-4xl lg:text-[42px] leading-[1] tracking-tight mb-1">
        {tier.price}
      </div>

      <p className={`font-space text-sm sm:text-base mb-6 ${bestForColor}`}>
        {tier.bestFor}
      </p>

      <div className={`h-px w-full mb-6 ${dividerColor}`} />

      <ul className="space-y-3 mb-8 flex-1">
        {tier.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm sm:text-base leading-snug">
            <svg
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${checkColor}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        to={`/contact?service=${encodeURIComponent(slug)}&tier=${encodeURIComponent(tier.name)}`}
        className={`mt-auto inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 sm:px-6 sm:py-3.5 font-bricolage font-semibold text-base uppercase tracking-tight hover:scale-[1.03] transition-all duration-300 ${ctaClasses}`}
      >
        <span>Request a quote</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </Link>
    </div>
  );
};

const AddonsRow = ({ addons, slug, isDark }) => {
  const labelColor = isDark ? 'text-licht/60' : 'text-mainkleur/60';
  const subLabelColor = isDark ? 'text-licht/50' : 'text-mainkleur/50';
  const cardClasses = isDark
    ? 'bg-licht/10 hover:bg-licht hover:text-mainkleur border-licht/15 hover:border-licht text-licht'
    : 'bg-white/50 hover:bg-mainkleur hover:text-licht border-mainkleur/15 hover:border-mainkleur text-mainkleur';
  const indicativeColor = isDark
    ? 'text-licht/70 group-hover:text-mainkleur/80'
    : 'text-mainkleur/60 group-hover:text-licht/80';

  return (
    <div className="mt-6 sm:mt-7 lg:mt-8">
      <div className="flex items-baseline justify-between gap-3 mb-4 sm:mb-5">
        <h4 className={`font-space text-xs sm:text-sm uppercase tracking-[0.22em] ${labelColor}`}>
          Available add-ons
        </h4>
        <span className={`font-space text-[11px] sm:text-xs uppercase tracking-[0.18em] ${subLabelColor}`}>
          Mix &amp; match per package
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        {addons.map((addon) => (
          <Link
            key={addon.label}
            to={`/contact?service=${encodeURIComponent(slug)}&addon=${encodeURIComponent(addon.label)}`}
            className={`group flex items-center justify-between gap-3 border rounded-xl px-4 py-3 transition-all duration-300 ${cardClasses}`}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-[#f97b22] group-hover:text-[#fe9043] font-bricolage font-extrabold text-lg leading-none">+</span>
              <span className="font-space font-medium text-sm sm:text-[15px] leading-tight truncate">
                {addon.label}
              </span>
            </span>
            <span className={`flex-shrink-0 font-space text-xs sm:text-[13px] whitespace-nowrap ${indicativeColor}`}>
              {addon.indicative}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const ServiceBlock = ({ service, index }) => {
  const anchor = slugToAnchor(service.slug);
  // Web design (0) and Branding (2) get the dark "card" treatment with margin.
  // Motion design (1) and Logo design (3) flow on the cream page bg.
  const isDark = index % 2 === 0;
  const accent = SERVICE_ACCENTS[index % SERVICE_ACCENTS.length];

  const txt = isDark ? 'text-licht' : 'text-mainkleur';
  const txtFaint = isDark ? 'text-licht/40' : 'text-mainkleur/40';
  const eyebrowColor = isDark ? 'text-licht/60' : 'text-mainkleur/60';

  // Wrapper styling per block type
  const wrapperClasses = isDark
    ? 'mt-12 sm:mt-16 lg:mt-20 mx-2 sm:mx-4 lg:mx-6 bg-mainkleur rounded-3xl p-6 sm:p-10 lg:p-14 scroll-mt-32'
    : `mt-12 sm:mt-16 lg:mt-20 ${index === 0 ? 'pt-0' : 'pt-12 border-t border-mainkleur/15'} scroll-mt-32`;

  return (
    <section id={anchor} className={`relative ${wrapperClasses}`}>
      {/* Title row: icon + title with tool monograms inline */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 sm:gap-8 mb-10 sm:mb-12 lg:mb-14">
        <div className="flex items-end gap-4 sm:gap-5 lg:gap-6 flex-shrink-0 min-w-0">
          <span
            className="flex items-center justify-center w-[72px] h-[72px] sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl text-4xl sm:text-5xl lg:text-6xl leading-none flex-shrink-0"
            style={{ backgroundColor: `${accent}26`, boxShadow: `inset 0 0 0 1px ${accent}66` }}
          >
            {service.icon}
          </span>
          <h2 className={`font-anton text-[14vw] sm:text-[100px] lg:text-[140px] leading-[0.85] tracking-[-3px] sm:tracking-[-5px] lg:tracking-[-7px] uppercase ${txt}`}>
            {service.title}
          </h2>
        </div>

        <div className="flex flex-col items-start sm:items-end gap-3">
          <span className={`font-space text-xs uppercase tracking-[0.22em] ${eyebrowColor}`}>
            Built with
          </span>
          <div className="flex -space-x-2 sm:-space-x-2.5">
            {service.tools.map((tool) => (
              <ToolLogo key={tool.name} tool={tool} isDark={isDark} />
            ))}
          </div>
        </div>
      </div>

      {/* Description + Process side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12 sm:mb-14 lg:mb-16">
        <div className="lg:col-span-5">
          <span className={`flex items-center gap-2.5 font-space text-xs uppercase tracking-[0.22em] mb-4 ${eyebrowColor}`}>
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} aria-hidden="true" />
            Service {String(index + 1).padStart(2, '0')} / {String(services.length).padStart(2, '0')}
          </span>

          {/* Outcome line (Fase 4) — what the client actually gets, not just the spec. */}
          {service.outcome && (
            <p
              className="font-bricolage font-extrabold text-[22px] sm:text-[26px] lg:text-[30px] leading-[1.1] tracking-tight mb-4"
              style={{ color: accent }}
            >
              {service.outcome}
            </p>
          )}

          <p className={`font-space text-base sm:text-lg lg:text-[19px] leading-[1.5] ${txt}`}>
            {service.longDescription}
          </p>
        </div>

        <div className="lg:col-span-7">
          <h3 className={`font-space text-xs uppercase tracking-[0.22em] mb-5 ${eyebrowColor}`}>
            Process
          </h3>
          <ol className="space-y-3 sm:space-y-4">
            {service.process.map((step, i) => (
              <li key={i} className={`flex items-start gap-4 sm:gap-5 font-bricolage text-lg sm:text-xl lg:text-2xl font-medium leading-[1.2] tracking-tight ${txt}`}>
                <span className={`font-space text-sm sm:text-base mt-1.5 w-8 flex-shrink-0 ${txtFaint}`}>
                  0{i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-7 mt-6 sm:mt-8">
        {service.tiers.map((tier) => (
          <TierCard key={tier.name} tier={tier} slug={service.slug} isDark={isDark} />
        ))}
      </div>

      {/* Add-ons row, attached below the packages */}
      <AddonsRow addons={service.addons} slug={service.slug} isDark={isDark} />
    </section>
  );
};

// "Choose your lane" comparison (Fase 4) — a fast, scannable overview that lets
// a visitor self-select the right discipline before scrolling the full tiers.
const LaneCompare = () => (
  <section className="pb-4 sm:pb-8">
    <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2.5 mb-8 sm:mb-10">
        <span className="w-2.5 h-2.5 rounded-full bg-[#f97b22]" aria-hidden="true" />
        <h2 className="font-space text-mainkleur/70 text-xs sm:text-sm uppercase tracking-[0.22em]">
          Solutions that fit the brief — choose your lane
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {services.map((service, i) => {
          const accent = SERVICE_ACCENTS[i % SERVICE_ACCENTS.length];
          return (
            <a
              key={service.id}
              href={`#${slugToAnchor(service.slug)}`}
              className="group flex flex-col gap-4 bg-white/45 hover:bg-mainkleur border border-mainkleur/15 hover:border-mainkleur rounded-2xl p-5 sm:p-6 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <span
                  className="flex items-center justify-center w-12 h-12 rounded-xl text-2xl"
                  style={{ backgroundColor: `${accent}26`, boxShadow: `inset 0 0 0 1px ${accent}66` }}
                >
                  {service.icon}
                </span>
                <span className="font-space text-mainkleur/50 group-hover:text-licht/60 text-[11px] uppercase tracking-[0.16em] transition-colors duration-300">
                  {service.cost}
                </span>
              </div>

              <h3 className="font-bricolage font-extrabold text-mainkleur group-hover:text-licht text-2xl tracking-tight uppercase transition-colors duration-300">
                {service.title}
              </h3>

              <p className="font-space text-mainkleur/75 group-hover:text-licht/85 text-sm leading-[1.5] transition-colors duration-300 flex-1">
                {service.outcome}
              </p>

              <p className="font-space text-mainkleur/55 group-hover:text-licht/65 text-[13px] leading-[1.45] transition-colors duration-300 pt-3 border-t border-mainkleur/15 group-hover:border-licht/20">
                <span className="font-semibold">Best if:</span> {service.bestIf}
              </p>

              <span className="inline-flex items-center gap-2 font-space text-[#f97b22] group-hover:text-[#fe9043] text-xs uppercase tracking-[0.16em] transition-colors duration-300">
                <span>See packages</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </a>
          );
        })}
      </div>
    </div>
  </section>
);

// Ongoing-support / retainer card (Fase 4) — makes the partnership option
// visible instead of hiding it inside a single web add-on.
const RetainerCard = () => (
  <section className="pb-16 sm:pb-20 lg:pb-24">
    <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
      <div className="grain-overlay relative overflow-hidden rounded-3xl bg-mainkleur text-licht p-7 sm:p-10 lg:p-14">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 -right-16 w-72 h-72 rounded-full blur-[90px] opacity-30 bg-olive"
        />
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div className="max-w-[680px]">
            <span className="eyebrow text-licht/60 mb-4">Ongoing partnership</span>
            <h3 className="font-bricolage font-extrabold text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.02] tracking-tight mt-4">
              Need a designer on call, not just a one-off?
            </h3>
            <p className="font-space text-licht/70 text-[15px] sm:text-[17px] lg:text-[19px] leading-[1.5] mt-4">
              A monthly retainer keeps your site, brand and content moving — updates,
              new pages, social motion and tweaks, all under one predictable rate.
              Priority turnaround, no re-onboarding every time.
            </p>
            <div className="flex flex-wrap gap-2.5 mt-6">
              {['Web maintenance', 'Design on demand', 'Priority turnaround', 'Monthly check-ins'].map((perk) => (
                <span key={perk} className="font-space text-[13px] text-licht/85 border border-hairline-light rounded-full px-3.5 py-1.5">
                  {perk}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-4 flex-shrink-0">
            <div className="font-bricolage font-extrabold text-[32px] sm:text-[40px] leading-none tracking-tight text-licht">
              from €95<span className="text-licht/50 text-xl font-normal"> / mo</span>
            </div>
            <Link
              to="/contact?service=Web%20design"
              className="inline-flex items-center gap-3 bg-[#f97b22] hover:bg-[#fe9043] text-mainkleur rounded-full px-6 py-4 font-bricolage font-semibold text-base sm:text-lg uppercase tracking-tight hover:scale-[1.03] transition-all duration-300"
            >
              <span>Discuss a retainer</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Structured data (Fase 4): expose the service catalogue to search engines as a
// ProfessionalService with an OfferCatalog. Built from the same data that powers
// the page so pricing and offerings never drift out of sync.
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://portfolio.skdesign.be';

const SERVICES_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'SK Design — Sander Kuyken',
  url: `${SITE_URL}/services`,
  image: `${SITE_URL}/og-image.jpg`,
  description:
    'Web design, motion design, branding and logo design with transparent, tiered pricing.',
  areaServed: ['BE', 'EU'],
  priceRange: '€450 – €12.000',
  founder: { '@type': 'Person', name: 'Sander Kuyken' },
  makesOffer: services.map((service) => ({
    '@type': 'Offer',
    name: service.title,
    description: service.longDescription || service.description,
    category: service.slug,
    priceCurrency: 'EUR',
    priceSpecification: service.tiers?.length
      ? {
          '@type': 'PriceSpecification',
          minPrice: Math.min(...service.tiers.map((t) => t.priceMin || 0).filter(Boolean)),
          maxPrice: Math.max(...service.tiers.map((t) => t.priceMax || 0)),
          priceCurrency: 'EUR',
          valueAddedTaxIncluded: false,
        }
      : undefined,
    itemOffered: {
      '@type': 'Service',
      name: service.title,
      serviceType: service.title,
      provider: { '@type': 'Person', name: 'Sander Kuyken' },
    },
  })),
};

const ServicesPage = () => {
  const location = useLocation();

  // Smooth scroll to anchor when navigating with hash (e.g. /services#web-design)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [location.hash]);

  return (
    <>
      <SEO
        title="Services & Pricing"
        description="Web design, motion design, branding, and logo design — tiered packages with transparent pricing, timelines, and what's included. Built for ambitious products."
        path="/services"
        jsonLd={SERVICES_JSONLD}
      />
      <Navbar />

      <main className="bg-[#f2f3d9]">
        {/* Hero */}
        <section className="pt-28 sm:pt-32 lg:pt-40 pb-12 sm:pb-16 lg:pb-20">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-10 sm:gap-12 lg:gap-8">
              <div className="font-anton text-mainkleur text-[18vw] sm:text-[140px] lg:text-[220px] leading-[0.88] tracking-[-4px] sm:tracking-[-6px] lg:tracking-[-9px] uppercase">
                <SplitText
                  text="SERVICES"
                  splitType="words"
                  delay={150}
                  duration={1.4}
                  ease="power3.out"
                  from={splitTextFrom}
                  to={splitTextTo}
                  threshold={0}
                  rootMargin={"+50px"}
                />
              </div>

              <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 lg:items-end justify-between">
                <p className="font-space text-mainkleur text-lg sm:text-xl lg:text-[22px] leading-[1.4] max-w-[680px]">
                  A clear breakdown of every service — what's in each package, what it costs, and how long it takes.
                </p>

                <span className="font-space text-mainkleur/60 text-xs sm:text-sm uppercase tracking-[0.22em] whitespace-nowrap">
                  All prices excl. 21% VAT
                </span>
              </div>

              {/* FAQ hint — keeps people from missing the FAQ all the way at the bottom */}
              <a
                href="#faq"
                className="inline-flex items-center gap-2 self-start text-mainkleur/70 hover:text-[#f97b22] font-space text-xs sm:text-sm uppercase tracking-[0.18em] transition-colors duration-300"
              >
                <span>Got questions? Jump to FAQ</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </a>

              <div className="w-full h-px bg-mainkleur/30" />
            </div>
          </div>
        </section>

        {/* Choose your lane comparison (Fase 4) */}
        <LaneCompare />

        {/* Service blocks */}
        <section className="pb-20 sm:pb-28 lg:pb-32">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
            {services.map((service, i) => (
              <ServiceBlock key={service.id} service={service} index={i} />
            ))}
          </div>
        </section>

        {/* Ongoing-support / retainer (Fase 4) */}
        <RetainerCard />

        {/* Custom-quote band — plain, no border, no card */}
        <section className="pb-20 sm:pb-24 lg:pb-28">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 sm:gap-8 py-8 sm:py-10 lg:py-12 border-t border-mainkleur/15">
              <div className="font-bricolage font-extrabold text-mainkleur text-[28px] sm:text-[40px] lg:text-[52px] leading-[0.95] tracking-tight max-w-[760px]">
                Don't see your scope here?
                <span
                  className="block font-normal text-[18px] sm:text-[20px] lg:text-[22px] mt-3 leading-[1.4] text-mainkleur/70"
                  style={{ background: 'unset', backgroundColor: 'unset' }}
                >
                  Bigger, weirder, or somewhere in between — happy to scope a custom proposal that fits your timeline and budget.
                </span>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-[#f97b22] hover:bg-[#fe9043] text-mainkleur rounded-full px-6 py-4 sm:px-8 sm:py-5 font-bricolage font-semibold text-lg sm:text-xl lg:text-2xl uppercase tracking-tight hover:scale-[1.03] transition-all duration-300 flex-shrink-0 shadow-lg shadow-[#f97b22]/30"
              >
                <span>Get a custom quote</span>
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-32">
          <FAQSection />
        </section>

        {/* Compact footer — the custom-quote band above already acts as the
            page's closing CTA, so we drop the full "ready to work?" contact
            landing here and keep just the nav/footer. */}
        <ContactSection compact />
      </main>
    </>
  );
};

export default Transition(ServicesPage);
